import { useCallback } from 'react';
import { useAnalyticsEvents } from '@atlaskit/analytics-next';
import { useFlagsService } from '@atlassian/jira-flags';
import { STATUS_TYPE } from '@atlassian/jira-platform-field-config/src/index.tsx';
import { fireUIAnalytics } from '@atlassian/jira-product-analytics-bridge';
import type { GroupContextField } from '../types.tsx';
import {
	getCreatedIssueFieldValue,
	getIssueCreateHiddenFlag,
	getIssueCreateSuccessFlag,
	getStatusName,
} from './utils.tsx';
import type { CreatedIssue } from './types.tsx';

export type ShouldSkipIssuesConnectionUpdate = (
	groupContextField?: GroupContextField,
	createdIssue?: CreatedIssue | null,
) => boolean;

export const useShouldSkipIssuesConnectionUpdate = (): ShouldSkipIssuesConnectionUpdate => {
	const { showFlag } = useFlagsService();
	const { createAnalyticsEvent } = useAnalyticsEvents();

	return useCallback(
		(groupContextField?: GroupContextField, createdIssue?: CreatedIssue | null) => {
			if (!createdIssue) {
				return false;
			}

			const issueKey = createdIssue.key;
			const event = createAnalyticsEvent({});

			const matchesIssueSearch = createdIssue.matchesIssueSearch !== false;
			const hasGroupValueChanged =
				!!groupContextField &&
				getCreatedIssueFieldValue(createdIssue, groupContextField.fieldId) !==
					groupContextField.fieldValue;

			if (!matchesIssueSearch) {
				issueKey && showFlag(getIssueCreateHiddenFlag({ issueKey }));
			} else if (hasGroupValueChanged && groupContextField?.fieldType === STATUS_TYPE) {
				issueKey &&
					showFlag(
						getIssueCreateSuccessFlag({
							issueKey,
							statusName: getStatusName(createdIssue),
						}),
					);
			} else {
				issueKey && showFlag(getIssueCreateSuccessFlag({ issueKey }));
			}

			fireUIAnalytics(event, 'issueCreatedInListFlag shown', {
				matchesIssueSearch,
				groupIssueCreateFieldType: groupContextField?.fieldType || null,
				hasGroupValueChanged,
			});

			return hasGroupValueChanged;
		},
		[showFlag, createAnalyticsEvent],
	);
};
